import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Check, Loader2, CreditCard, Zap, Shield, Users, Star } from 'lucide-react';
import { PRODUCTS } from '../stripe-config';
import StripeCheckoutButton from '../components/StripeCheckoutButton';
import StripePortalButton from '../components/StripePortalButton';
import { toast } from '../lib/toast';

interface AccountTypeProps {
  navigate: (to: string) => void;
}

interface SubscriptionData {
  subscription_status: string;
  price_id: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
}

export default function AccountType({ navigate }: AccountTypeProps) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [billingInterval, setBillingInterval] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);

  useEffect(() => {
    fetchSubscription();
    
    // Check URL parameters for success or canceled
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccess(true);
      // Remove the query parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      setShowCanceled(true);
      // Remove the query parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('stripe_user_subscriptions')
        .select('*')
        .maybeSingle();
      
      if (error) throw error;
      
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = subscription?.subscription_status === 'active' || 
                       subscription?.subscription_status === 'trialing';
  
  const isExpiring = subscription?.cancel_at_period_end;
  
  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getPlanName = () => {
    if (!subscription?.price_id) return 'Free Plan';
    
    // Check if the price ID matches any of our products
    for (const category in PRODUCTS) {
      for (const interval in PRODUCTS[category as keyof typeof PRODUCTS]) {
        const product = PRODUCTS[category as keyof typeof PRODUCTS][interval as 'MONTHLY' | 'YEARLY'];
        if (product.priceId === subscription.price_id) {
          return product.name;
        }
      }
    }
    
    return 'Custom Plan';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Account & Billing</h1>

          {showSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Check className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Payment successful
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      Thank you for your payment. Your subscription has been activated.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showCanceled && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Loader2 className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Payment canceled
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Your payment was canceled. You can try again whenever you're ready.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Current Plan */}
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg mb-8">
                <div className="px-4 py-5 sm:p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                    Current Plan
                  </h2>
                  <div className="mt-2 max-w-xl text-sm text-gray-500 dark:text-gray-400">
                    <p>
                      You are currently on the <span className="font-medium text-gray-900 dark:text-white">{getPlanName()}</span>.
                      {isSubscribed && !isExpiring && (
                        <> Your subscription will automatically renew on {formatDate(subscription?.current_period_end)}.</>
                      )}
                      {isSubscribed && isExpiring && (
                        <> Your subscription will end on {formatDate(subscription?.current_period_end)}.</>
                      )}
                    </p>
                  </div>
                  <div className="mt-5">
                    {isSubscribed ? (
                      <StripePortalButton
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Manage Subscription
                      </StripePortalButton>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Choose a plan below to upgrade your account.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing Plans */}
              <div className="space-y-8">
                <div className="flex justify-center">
                  <div className="relative bg-white dark:bg-gray-800 p-0.5 rounded-lg flex">
                    <button
                      type="button"
                      className={`relative py-2 px-6 border-transparent rounded-md text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:z-10 ${
                        billingInterval === 'MONTHLY'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setBillingInterval('MONTHLY')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`relative py-2 px-6 border-transparent rounded-md text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:z-10 ${
                        billingInterval === 'YEARLY'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setBillingInterval('YEARLY')}
                    >
                      Yearly <span className="text-xs font-normal">(Save 17%)</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Free Plan */}
                  <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Free</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Basic features for personal use
                      </p>
                      <p className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">$0</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">forever free</p>
                      <ul className="mt-6 space-y-4">
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                            Up to 3 projects
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                            Up to 20 tasks per project
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                            Basic AI features
                          </p>
                        </li>
                        <li className="flex items-start">
                          <div className="flex-shrink-0">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                            Community support
                          </p>
                        </li>
                      </ul>
                      <div className="mt-8">
                        <button
                          disabled
                          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                        >
                          Current Plan
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Pro Plan */}
                  <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden border-2 border-indigo-500">
                    <div className="bg-indigo-500 py-2">
                      <p className="text-center text-sm font-medium text-white">MOST POPULAR</p>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pro</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Advanced features for professionals
                      </p>
                      <p className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">
                        {PRODUCTS.PRO[billingInterval].price}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {billingInterval === 'MONTHLY' ? 'per month' : 'per year'}
                      </p>
                      <ul className="mt-6 space-y-4">
                        {PRODUCTS.PRO[billingInterval].features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <div className="flex-shrink-0">
                              <Check className="h-5 w-5 text-green-500" />
                            </div>
                            <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                              {feature}
                            </p>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-8">
                        {subscription?.price_id === PRODUCTS.PRO[billingInterval].priceId ? (
                          <button
                            disabled
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                          >
                            Current Plan
                          </button>
                        ) : (
                          <StripeCheckoutButton
                            plan="PRO"
                            interval={billingInterval}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            {isSubscribed ? 'Change Plan' : 'Upgrade to Pro'}
                          </StripeCheckoutButton>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Business Plan */}
                  <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Business</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Enterprise-grade features for teams
                      </p>
                      <p className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">
                        {PRODUCTS.BUSINESS[billingInterval].price}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {billingInterval === 'MONTHLY' ? 'per month' : 'per year'}
                      </p>
                      <ul className="mt-6 space-y-4">
                        {PRODUCTS.BUSINESS[billingInterval].features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <div className="flex-shrink-0">
                              <Check className="h-5 w-5 text-green-500" />
                            </div>
                            <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                              {feature}
                            </p>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-8">
                        {subscription?.price_id === PRODUCTS.BUSINESS[billingInterval].priceId ? (
                          <button
                            disabled
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed"
                          >
                            Current Plan
                          </button>
                        ) : (
                          <StripeCheckoutButton
                            plan="BUSINESS"
                            interval={billingInterval}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <Users className="h-4 w-4 mr-2" />
                            {isSubscribed ? 'Change Plan' : 'Upgrade to Business'}
                          </StripeCheckoutButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Features Comparison */}
              <div className="mt-16">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Plan Features Comparison</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Feature
                        </th>
                        <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Free
                        </th>
                        <th className="px-6 py-3 bg-indigo-50 dark:bg-indigo-900/20 text-center text-xs font-medium text-indigo-500 dark:text-indigo-300 uppercase tracking-wider">
                          Pro
                        </th>
                        <th className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Business
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          Projects
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          Up to 3
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white bg-indigo-50 dark:bg-indigo-900/10">
                          Unlimited
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                          Unlimited
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          Tasks
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          Up to 20 per project
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white bg-indigo-50 dark:bg-indigo-900/10">
                          Unlimited
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                          Unlimited
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          AI Features
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          Basic
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white bg-indigo-50 dark:bg-indigo-900/10">
                          Advanced
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                          Advanced
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          Team Members
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          1
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white bg-indigo-50 dark:bg-indigo-900/10">
                          Up to 3
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                          Unlimited
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          Support
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                          Community
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white bg-indigo-50 dark:bg-indigo-900/10">
                          Priority
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                          Dedicated
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}